import React, { useCallback, useEffect, useState } from "react";
import { ShieldWarning, Crosshair } from "@phosphor-icons/react";
import { http } from "@/lib/api";

// Sprint 62.8 — Coverage Guard: how much of the PLAYABLE pool the competitor
// consensus actually covers. Under 50% the shadow field is mostly our own
// estimate, so Generate asks for a one-click confirm.
export const COVERAGE_MIN_PCT = 50;

export function useFieldCoverage(dep) {
  const [coverage, setCoverage] = useState(null);
  const ref = React.useRef(null);
  const reload = useCallback(() => http.get("/competitors/coverage")
    .then((r) => { ref.current = r.data; setCoverage(r.data); return r.data; })
    .catch(() => { setCoverage(null); return null; }), []);
  useEffect(() => { reload(); }, [reload, dep]);
  // Resolves immediately when loaded; otherwise fetches first (Generate clicked right after mount).
  const ensureCoverage = useCallback(() => (ref.current ? Promise.resolve(ref.current) : reload()), [reload]);
  return { coverage, reload, ensureCoverage };
}

export const coverageIsLow = (cov) => !!cov && (cov.n_sources === 0 || Number(cov.coverage_pct || 0) < COVERAGE_MIN_PCT);

// Confirm gate fires when sites are loaded but thin, or (admins only) when no site is loaded at all.
export const needsCoverageConfirm = (cov, isAdmin) => {
  if (!cov) return false;
  if (cov.n_sources > 0) return Number(cov.coverage_pct || 0) < COVERAGE_MIN_PCT;
  return !!isAdmin;
};

export function FieldCoverageBadge({ coverage, compact = false, testId = "field-model-badge" }) {
  if (!coverage) {
    return <span className="text-zinc-600" data-testid={testId}>Field model: …</span>;
  }
  const pct = Number(coverage.coverage_pct || 0);
  const low = coverageIsLow(coverage);
  const sites = coverage.sources || [];
  const perSite = coverage.per_site || {};
  const weights = coverage.weights?.proj || {};
  const hasWeights = Number(coverage.weights?.n_weeks || 0) > 0 && Object.keys(weights).length > 0;
  const color = coverage.n_sources === 0 ? "text-zinc-500" : low ? "text-[#FF3B30]" : "text-[#FFCC00]";
  const title = sites.length
    ? sites.map((s) => `${s}: ${perSite[s] ?? 0}/${coverage.n_playable} playable${hasWeights && weights[s] != null ? ` · trust ${Math.round(weights[s] * 100)}%` : ""}`).join("\n")
      + (hasWeights ? `\nTrust weights from ${coverage.weights.n_weeks} graded week${coverage.weights.n_weeks === 1 ? "" : "s"}` : "\nEqual site weights until a week is graded")
    : "Load competitor projections in Admin → Field Model";
  return (
    <span className={`inline-flex items-center gap-1.5 ${color} ${low && sites.length ? "font-bold" : ""}`} data-testid={testId} title={title}>
      {low && sites.length ? <ShieldWarning size={12} weight="fill" /> : <Crosshair size={12} weight="fill" />}
      {sites.length ? (
        <>
          <span data-testid="field-coverage-pct">Field model: {pct.toFixed(0)}% of playable pool covered</span>
          <span className="text-zinc-500">· {sites.length} site{sites.length === 1 ? "" : "s"}</span>
          {!compact && (
            <span className="text-zinc-500">
              · {sites.map((s) => `${s} ${perSite[s] ?? 0}`).join(" · ")}
              {hasWeights && <> · trust {sites.map((s) => `${s.slice(0, 3)} ${Math.round((weights[s] ?? 0) * 100)}%`).join(" / ")}</>}
            </span>
          )}
          {low && <span data-testid="field-coverage-warning">— below {COVERAGE_MIN_PCT}%: shadow field falls back to our internal estimate</span>}
          {coverage.field_model?.bleed && <span className="text-[#FF3B30] font-bold" data-testid="field-coverage-bleed">— entered for slate {coverage.field_model.from_dg}, not this one: clear and re-enter</span>}
        </>
      ) : (
        <span>Field model: internal estimate{compact ? "" : " (load competitor projections in Admin to build around their lineups)"}</span>
      )}
    </span>
  );
}

export function CoverageConfirmDialog({ open, coverage, onConfirm, onCancel }) {
  if (!open) return null;
  const pct = Number(coverage?.coverage_pct || 0);
  const none = !coverage || coverage.n_sources === 0;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="coverage-confirm-dialog">
      <div className="w-full max-w-md bg-[#121212] border border-[#FF3B30]/60 p-5" style={{ boxShadow: "0 0 30px rgba(255,59,48,0.25)" }}>
        <div className="flex items-center gap-2 text-[#FF3B30] mono text-[10px] uppercase tracking-widest mb-2">
          <ShieldWarning size={14} weight="fill" /> Coverage Guard
        </div>
        <h3 className="heading text-white font-black text-xl leading-tight mb-2">
          {none ? "No competitor consensus loaded" : `Only ${pct.toFixed(0)}% of the playable pool is covered`}
        </h3>
        <p className="text-[12px] text-zinc-400 leading-relaxed mb-4">
          {none
            ? "The shadow field will be our internal estimate of what the field builds. Load the competitor projections in Admin → Field Model for a sharper dup estimate and evasion."
            : `Below ${COVERAGE_MIN_PCT}% the engine ignores the competitor numbers and falls back to our internal shadow field. Field Win % and Copies will reflect our guess of the field, not the sites' actual builds.`}
        </p>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCancel} data-testid="coverage-confirm-cancel"
            className="mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-zinc-800 px-3 py-2">
            Go back
          </button>
          <button type="button" onClick={onConfirm} data-testid="coverage-confirm-build"
            className="mono text-[10px] uppercase tracking-widest bg-[#FF3B30] hover:bg-[#e0332a] text-black font-black px-3 py-2">
            Build anyway
          </button>
        </div>
      </div>
    </div>
  );
}
